'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { t } from 'mobx-state-tree';

import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import type { ComboboxGroup } from '@/components/ui/combobox';

import { nameHelpers } from '@/editor/core';
import { editorStore } from '@/editor/store';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Resolve a class name (short or full) to a constructor using the ClassRegistry.
 * Returns null if the class cannot be found.
 */
function resolveClass(className: string): (new (...args: any[]) => any) | null {
  return ENGINE.ClassRegistry.getClassByConstructorName(className);
}

/**
 * Check whether an object is an instance of the class identified by className.
 * Falls back to an exact constructor-name comparison when the class cannot be
 * resolved through the registry (e.g. unregistered game classes).
 */
function isInstanceOfClass(obj: object, className: string): boolean {
  const ctor = resolveClass(className);
  if (ctor) {
    return obj instanceof ctor;
  }
  // Fallback: exact constructor name match
  return (obj as any).constructor?.name === className;
}

/** Group actors by their class for the combobox */
function groupActorsByClass(actors: ENGINE.Actor[]): ComboboxGroup[] {
  const groups = new Map<string, { label: string, value: string }[]>();

  for (const actor of actors) {
    const className = actor.constructor.name;
    const option = {
      label: nameHelpers.getActorDisplayName(actor),
      value: actor.uuid,
    };

    if (!groups.has(className)) {
      groups.set(className, []);
    }
    groups.get(className)!.push(option);
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, options]) => ({
      label,
      options: options.sort((a, b) => a.label.localeCompare(b.label))
    }));
}

/** Group components by their class for the combobox */
function groupComponentsByClass(components: ENGINE.SceneComponent[]): ComboboxGroup[] {
  const groups = new Map<string, { label: string, value: string }[]>();

  for (const component of components) {
    const className = component.constructor.name;
    const option = {
      label: nameHelpers.getComponentDisplayName(component),
      value: component.uuid,
    };

    if (!groups.has(className)) {
      groups.set(className, []);
    }
    groups.get(className)!.push(option);
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, options]) => ({
      label,
      options: options.sort((a, b) => a.label.localeCompare(b.label))
    }));
}

// ---------------------------------------------------------------------------
// ReferenceProperty base
//
// IMPORTANT: We store only the UUID string (or null) inside the MST model.
// MobX-State-Tree calls Object.freeze() on t.frozen values, which would
// freeze the live engine object (Actor / SceneComponent) and make Three.js
// properties like `matrixWorldNeedsUpdate` read-only — causing render crashes.
//
// The live object reference is kept exclusively in volatile state so MST
// never touches it.
// ---------------------------------------------------------------------------

/** Base model for reference properties (actor/component references) */
export const ReferenceProperty = t.compose('ReferenceProperty', PropertyBase,
  t.model({
    /** UUID of the referenced object, or null when nothing is selected. */
    valueKey: t.maybeNull(t.string),
    /** UUID of the default referenced object. */
    defaultValueKey: t.maybeNull(t.string),
    referenceClass: t.maybe(t.string), // Class name filter (e.g., 'Actor', 'ModelMeshActor')
  })
).volatile(() => ({
  /** The live engine object (Actor | SceneComponent | null). Never stored in MST. */
  _liveValue: null as ENGINE.Actor | ENGINE.SceneComponent | null,
})).views(self => ({
  /**
   * The actual live reference. Consumers should use this for all engine
   * interactions. It is kept in sync with valueKey by setValue().
   */
  get value(): ENGINE.Actor | ENGINE.SceneComponent | null {
    return self._liveValue;
  },
  /**
   * Compatibility shim so PropertyBase's generic `'defaultValue' in self` check
   * succeeds. Actual default tracking uses defaultValueKey (UUID string).
   */
  get defaultValue(): null {
    return null;
  },
  get hasValueChanged(): boolean {
    return self.valueKey !== self.defaultValueKey;
  },
  get canResetToDefaultValue(): boolean {
    if ((self as any).readOnly) return false;
    return this.hasValueChanged;
  },
  get displayValue(): string {
    const v = self._liveValue;
    if (!v) return '';
    if (v instanceof ENGINE.Actor) {
      return nameHelpers.getActorDisplayName(v);
    }
    if (v instanceof ENGINE.SceneComponent) {
      return nameHelpers.getComponentDisplayName(v);
    }
    return (v as any).constructor?.name ?? 'Object';
  },
})).actions(self => ({
  /**
   * Set the referenced object.
   * @param value  The live engine object (or null to clear).
   * @param fromPanel  True when the change originates from the UI.
   * @param initial    True during initial population (suppresses undo entry).
   */
  setValue(value: ENGINE.Actor | ENGINE.SceneComponent | null, fromPanel = true, initial = false) {
    if (value === self._liveValue) return;
    self._liveValue = value;
    self.valueKey = value?.uuid ?? null;
    // Emit the live object so downstream onChange handlers receive the actual
    // reference (not the UUID string) and can assign it directly to the target.
    self.notifyValueChange(self.path, value, { fromPanel, initial, property: self as any });
  },
  setDefaultValue(value: ENGINE.Actor | ENGINE.SceneComponent | null) {
    self.defaultValueKey = value?.uuid ?? null;
  },
  setReferenceClass(className: string) {
    self.referenceClass = className;
  },
  /**
   * Override PropertyBase.resetToDefault to resolve the default live object
   * from defaultValueKey rather than using the generic defaultValue field.
   */
  resetToDefault() {
    const resolved = (self as any).resolveLiveValueFromKey(self.defaultValueKey);
    (self as any).setValue(resolved, true, false);
  },
  /**
   * Restore the live reference from a UUID string.
   * Used when the property store reloads the value from the engine (e.g. after
   * undo/redo) and hands us a UUID rather than a live object.
   * Subclasses override this to perform the actual UUID → object lookup.
   */
  resolveLiveValueFromKey(_key: string | null): ENGINE.Actor | ENGINE.SceneComponent | null {
    return null;
  },
}));

// ---------------------------------------------------------------------------
// ActorReferenceProperty
// ---------------------------------------------------------------------------

/** Model for actor reference property */
export const ActorReferenceProperty = t.compose('ActorReferenceProperty', ReferenceProperty,
  t.model({
    type: t.literal(PropertyType.ActorReference),
  })
).views(self => ({
  /** The actor or component that owns this property */
  get owner(): ENGINE.Actor | ENGINE.SceneComponent | undefined {
    if (!self.store) return undefined;
    return self.store.sources[0]?.target;
  },
  /** The owner actor (if the property is on an actor) */
  get ownerActor(): ENGINE.Actor | undefined {
    const owner = this.owner;
    if (owner instanceof ENGINE.Actor) return owner;
    return undefined;
  },
  /** Filtered, deduplicated list of candidate actors */
  get candidateActors(): ENGINE.Actor[] {
    const actors = editorStore.scene.getSceneActors();

    const filtered = self.referenceClass
      ? actors.filter(actor => isInstanceOfClass(actor, self.referenceClass!))
      : actors;

    const ownerUuid = this.ownerActor?.uuid;
    return ownerUuid
      ? filtered.filter(actor => actor.uuid !== ownerUuid)
      : filtered;
  },
  get selectOptions(): { label: string, value: string }[] {
    return this.candidateActors.map(actor => ({
      label: nameHelpers.getActorDisplayName(actor),
      value: actor.uuid,
    }));
  },
  get groups(): ComboboxGroup[] {
    return groupActorsByClass(this.candidateActors);
  },
})).actions(self => ({
  /** Resolve a UUID to a live Actor from the current world. */
  resolveLiveValueFromKey(key: string | null): ENGINE.Actor | null {
    if (!key) return null;
    return editorStore.scene.world?.getActorByUuid(key) ?? null;
  },
  /** Set value by UUID key (called from UI combobox) */
  setValueByKey(key: string) {
    if (!key) {
      self.setValue(null, true, false);
      return;
    }
    const actor = editorStore.scene.world?.getActorByUuid(key) ?? null;
    if (actor) {
      self.setValue(actor, true, false);
    } else {
      console.warn('[ActorReferenceProperty] Actor not found for key:', key);
    }
  },
}));

// ---------------------------------------------------------------------------
// ComponentReferenceProperty
// ---------------------------------------------------------------------------

/** Model for component reference property */
export const ComponentReferenceProperty = t.compose('ComponentReferenceProperty', ReferenceProperty,
  t.model({
    type: t.literal(PropertyType.ComponentReference),
  })
).views(self => ({
  /** The actor that owns the property with this component reference */
  get ownerActor(): ENGINE.Actor | undefined {
    if (!self.store) return undefined;
    const target = self.store.sources[0]?.target;

    if (target instanceof ENGINE.Actor) return target;
    if (target instanceof ENGINE.SceneComponent) {
      return target.getActor() ?? undefined;
    }
    return undefined;
  },
  /** The owner component (if the property is on a component) */
  get ownerComponent(): ENGINE.SceneComponent | undefined {
    if (!self.store) return undefined;
    const target = self.store.sources[0]?.target;
    if (target instanceof ENGINE.SceneComponent) return target;
    return undefined;
  },
  /** Filtered, deduplicated list of candidate components */
  get candidateComponents(): ENGINE.SceneComponent[] {
    const ownerActor = this.ownerActor;
    if (!ownerActor) return [];

    const components = ownerActor.getComponents(ENGINE.SceneComponent);

    const filtered = self.referenceClass
      ? components.filter(c => isInstanceOfClass(c, self.referenceClass!))
      : components;

    const ownerUuid = this.ownerComponent?.uuid;
    return ownerUuid
      ? filtered.filter(c => c.uuid !== ownerUuid)
      : filtered;
  },
  get selectOptions(): { label: string, value: string }[] {
    return this.candidateComponents.map(c => ({
      label: nameHelpers.getComponentDisplayName(c),
      value: c.uuid,
    }));
  },
  get groups(): ComboboxGroup[] {
    return groupComponentsByClass(this.candidateComponents);
  },
  /** Get the UUID key for the current value (for combobox binding) */
  get currentValueKey(): string {
    return self.valueKey ?? '';
  },
})).actions(self => ({
  /** Resolve a UUID to a live SceneComponent from the owner actor. */
  resolveLiveValueFromKey(key: string | null): ENGINE.SceneComponent | null {
    if (!key) return null;
    const ownerActor = self.store?.sources[0]?.target;
    let actor: ENGINE.Actor | undefined;
    if (ownerActor instanceof ENGINE.Actor) {
      actor = ownerActor;
    } else if (ownerActor instanceof ENGINE.SceneComponent) {
      actor = ownerActor.getActor() ?? undefined;
    }
    if (!actor) return null;
    return actor.getComponents(ENGINE.SceneComponent).find(c => c.uuid === key) ?? null;
  },
  /** Set value by UUID key (called from UI combobox) */
  setValueByKey(key: string) {
    if (!key) {
      self.setValue(null, true, false);
      return;
    }
    const component = (this as any).resolveLiveValueFromKey(key);
    if (component) {
      self.setValue(component, true, false);
    } else {
      console.warn('[ComponentReferenceProperty] Component not found for key:', key);
    }
  },
}));
